import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import StaticAdsService from 'services/StaticAdsService';

// What each piece of work cost. Two currencies, deliberately not converted:
// LLM calls (the Anthropic API key) are dollars priced from token usage;
// image generations spend Higgsfield consumer credits, which have no dollar
// value — so credits are shown as credits, full stop. Loads on demand (no
// polling — cost data is not live-critical and an idle poll keeps Snowflake
// warm).

interface CostRow {
  createdAt: string;
  label: string;
  refId: string | null;
  model: string | null;
  tokensIn: number | null;
  tokensOut: number | null;
  costUsd: number | null;
  credits: number | null;
}

interface CostsResponse {
  balance: number | null;
  totals: { usdAllTime: number; usdWeek: number; creditsAllTime: number; creditsWeek: number };
  rows: CostRow[];
}

// Nearest tenth of a cent — enough precision to compare edits, no noise.
const usd = (n: number | null | undefined) => (n == null ? '—' : `$${Number(n).toFixed(3)}`);

function SummaryCard({ title, value, hint }: { title: string; value: string; hint?: string }) {
  const card = (
    <Card variant="outlined" sx={{ p: 1.5, minWidth: 170 }}>
      <Typography variant="caption" color="text.secondary">{title}</Typography>
      <Typography variant="h6">{value}</Typography>
    </Card>
  );
  return hint ? <Tooltip title={hint}>{card}</Tooltip> : card;
}

export default function CostsTab() {
  const service = useMemo(() => new StaticAdsService(), []);
  const [data, setData] = useState<CostsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  // A failed load must not read as "nothing was spent" — this is the money
  // tab, and an empty table is a claim, not an absence of one.
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await service.getCosts());
    } catch (err: any) {
      console.error('Error loading static creative costs', err);
      setError(err?.response?.data?.error || err?.message || 'Could not load costs');
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => { void load(); }, [load]);

  return (
    <Box>
      {error && (
        <Alert
          severity="error" sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={load} disabled={loading}>Retry</Button>}
        >
          Couldn&apos;t load costs — {error}.{' '}
          {data
            ? 'The figures below are from the last successful load, not current.'
            : 'The dashes below mean “not loaded”, not “nothing spent”.'}
        </Alert>
      )}

      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 1.5, alignItems: 'stretch' }}>
        <SummaryCard
          title="Higgsfield credits remaining"
          value={data?.balance == null ? '—' : String(data.balance)}
          hint="Live from the Higgsfield account (consumer pool — credits have no dollar value)"
        />
        <SummaryCard
          title="Credits spent (recorded)"
          value={data ? `${data.totals.creditsWeek} / 7d · ${data.totals.creditsAllTime} total` : '—'}
          hint="Measured per generation as a balance delta around the job — approximate when two jobs finish together"
        />
        <SummaryCard
          title="LLM spend (API key)"
          value={data ? `${usd(data.totals.usdWeek)} / 7d · ${usd(data.totals.usdAllTime)} total` : '—'}
          hint="Token-priced from the model price table: analyses, build sheets, text layout, effects, selling points"
        />
        <Box sx={{ flex: 1 }} />
        <Button
          size="small" startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={load} disabled={loading} sx={{ alignSelf: 'center' }}
        >
          Refresh
        </Button>
      </Stack>

      {!data && loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}

      {data && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>What</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell>Model</TableCell>
              <TableCell align="right">Tokens in / out</TableCell>
              <TableCell align="right">Cost</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.rows.map((row, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableRow key={i} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(row.createdAt).toLocaleString()}</TableCell>
                <TableCell>{row.label}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                  {row.refId ? `${row.refId.slice(0, 8)}…` : '—'}
                </TableCell>
                <TableCell>{row.model || '—'}</TableCell>
                <TableCell align="right">
                  {row.tokensIn != null ? `${row.tokensIn} / ${row.tokensOut ?? '—'}` : '—'}
                </TableCell>
                <TableCell align="right">
                  {row.credits != null ? `${row.credits} credit${row.credits === 1 ? '' : 's'}` : usd(row.costUsd)}
                </TableCell>
              </TableRow>
            ))}
            {!data.rows.length && (
              <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.disabled">No recorded costs yet.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
